import { createContext, useContext } from 'react'

/**
 * The admin session — the one piece of state every route in this panel depends
 * on.
 *
 * Kept in a plain `.ts` file, separate from the provider component, because
 * `react-refresh/only-export-components` is an error here (`--max-warnings 0`)
 * and a file that exports both a component and a hook loses fast refresh.
 */

/**
 * WHY THIS IS A FIVE-STATE MACHINE AND NOT A BOOLEAN.
 *
 * "Signed in" and "allowed in" are different questions with different answers,
 * and collapsing them is what produces the two failure modes this guard exists
 * to avoid:
 *
 *   • a redirect loop — /login sends an authenticated non-admin to /, / sends
 *     them back to /login, forever, because both only knew `!isAdmin`;
 *   • a blank page — the check is still in flight, nothing renders, and the
 *     operator is looking at white.
 *
 * So `signed-out` (go to the login form) and `denied` (you are signed in and
 * this account is not an admin — stop here and say so) are separate states, and
 * `loading` is a state rather than the absence of one.
 */
export type AdminSessionStatus =
  /** The session and the admin check are still resolving. Render a skeleton. */
  | 'loading'
  /** No `VITE_SUPABASE_URL` / `VITE_SUPABASE_ANON_KEY`. Nothing can work. */
  | 'unconfigured'
  /** No Supabase session. The login form is the right destination. */
  | 'signed-out'
  /** Signed in, but the database says this account is not an active admin. */
  | 'denied'
  /** Signed in and `public.is_admin()` returned true. */
  | 'authorized'

export type AdminDeniedReason =
  /** `is_admin()` returned false. The honest, expected answer today. */
  | 'not-admin'
  /** The check itself failed — offline, RPC missing, schema cache stale. */
  | 'check-failed'

export type AdminSessionValue = {
  status: AdminSessionStatus
  /** The authenticated user's id, or null. Never a display value. */
  userId: string | null
  /** Shown on the no-access screen so the operator knows WHICH account failed. */
  email: string | null
  /** Set only while `status === 'denied'`. */
  deniedReason: AdminDeniedReason | null
  /**
   * The raw failure behind `check-failed` or `unconfigured`. Always rendered
   * alongside an explanation, never as the whole message.
   */
  errorMessage: string | null
  /** Throws `AdminSignInError` on failure so the form can name the field. */
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  /** Re-run the admin check. The retry on the `check-failed` screen. */
  recheck: () => void
}

/**
 * Why `signIn` throws a typed error rather than returning a string: the login
 * form owns the copy (hard rule 5 — every display string goes through i18n) and
 * this layer owns the classification. Passing a Supabase message straight to
 * the screen would put untranslated English in front of a Norwegian operator.
 */
export type AdminSignInErrorCode =
  | 'invalid-credentials'
  | 'email-not-confirmed'
  | 'rate-limited'
  | 'not-configured'
  | 'unknown'

export class AdminSignInError extends Error {
  readonly code: AdminSignInErrorCode

  constructor(code: AdminSignInErrorCode, message: string) {
    super(message)
    this.name = 'AdminSignInError'
    this.code = code
  }
}

export const AdminSessionContext = createContext<AdminSessionValue | null>(null)

export function useAdminSession(): AdminSessionValue {
  const value = useContext(AdminSessionContext)
  if (!value) throw new Error('useAdminSession must be used inside an <AdminSessionProvider>')
  return value
}
